import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Check, ArrowRight, Mail, ArrowLeft, RefreshCw, Lock, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { RotalieLogo } from "../components/RotalieLogo";

// Step indicator component
const StepIndicator = ({ step }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {[1, 2, 3].map((s) => (
      <div key={s} className="flex items-center">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
          step >= s 
            ? "bg-gradient-to-r from-violet-500 to-teal-400 text-white" 
            : "bg-slate-200 text-slate-500"
        }`}>
          {step > s ? <Check className="w-4 h-4" /> : s}
        </div>
        {s < 3 && (
          <div className={`w-12 h-0.5 mx-1 ${step > s ? "bg-gradient-to-r from-violet-500 to-teal-400" : "bg-slate-200"}`} />
        )}
      </div>
    ))}
  </div>
);

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Check if this is staff mode (via URL param ?type=staff)
  const isStaffMode = searchParams.get('type') === 'staff';
  
  // Form step: 1 = email, 2 = verify code, 3 = new password
  const [step, setStep] = useState(1);
  
  // Form fields
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // State
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputRefs = useRef([]);

  // API endpoints based on mode
  const endpoints = isStaffMode ? {
    forgotPassword: "/auth/staff-forgot-password",
    resendCode: "/auth/staff-resend-reset-code",
    verifyCode: "/auth/staff-verify-reset-code",
    resetPassword: "/auth/staff-reset-password"
  } : {
    forgotPassword: "/auth/forgot-password",
    resendCode: "/auth/resend-reset-code",
    verifyCode: "/auth/verify-reset-code",
    resetPassword: "/auth/reset-password"
  };

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Handle sending reset code
  const handleSendResetCode = async () => {
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    setSendingCode(true);
    try {
      await api.post(endpoints.forgotPassword, {
        email: email.toLowerCase()
      });
      toast.success("Reset code sent! Check your email.");
      setStep(2);
      setResendCooldown(60);
      setTimeout(() => codeInputRefs.current[0]?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset code");
    } finally {
      setSendingCode(false);
    }
  };

  // Handle resending code
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    setSendingCode(true);
    try {
      await api.post(endpoints.resendCode, {
        email: email.toLowerCase()
      });
      toast.success("New reset code sent!");
      setResendCooldown(60);
      setVerificationCode(["", "", "", "", "", ""]);
      codeInputRefs.current[0]?.focus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend code");
    } finally {
      setSendingCode(false);
    }
  };

  // Handle verification code input
  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...verificationCode];
    newCode[index] = value.slice(-1);
    setVerificationCode(newCode);

    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }

    if (newCode.every(d => d) && newCode.join("").length === 6) {
      handleVerifyCode(newCode.join(""));
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split("");
      setVerificationCode(newCode);
      handleVerifyCode(pastedData);
    }
  };

  // Verify the code
  const handleVerifyCode = async (code) => {
    setVerifying(true);
    try {
      await api.post(endpoints.verifyCode, {
        email: email.toLowerCase(),
        code: code
      });
      toast.success("Code verified!");
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid or expired code");
      setVerificationCode(["", "", "", "", "", ""]);
      codeInputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  // Reset password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await api.post(endpoints.resetPassword, {
        email: email.toLowerCase(),
        code: verificationCode.join(""),
        new_password: newPassword
      });
      toast.success("Password reset successfully! Please sign in.");
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="p-6">
        <Link to="/">
          <RotalieLogo />
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Staff mode indicator */}
          {isStaffMode && (
            <div className="mb-4 text-center">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-700">
                Staff Portal
              </span>
            </div>
          )}
          
          <StepIndicator step={step} />

          {/* Step 1: Enter Email */}
          {step === 1 && (
            <>
              <div className="mb-8 text-center">
                <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="w-8 h-8 text-violet-600" />
                </div>
                <h1 className="text-3xl font-bold text-slate-900 font-['Inter'] mb-2">Forgot Password?</h1>
                <p className="text-slate-500">
                  {isStaffMode 
                    ? "Enter your email address and we'll send you a reset code."
                    : "No worries! Enter your email and we will send you a reset code."}
                </p>
              </div>

              <div className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-violet-500 focus:ring-violet-500 rounded-xl transition-colors"
                    data-testid="forgot-email-input"
                  />
                </div>
                
                <Button
                  onClick={handleSendResetCode}
                  className="w-full h-14 bg-gradient-to-r from-violet-500 to-teal-400 hover:from-violet-600 hover:to-teal-500 text-white rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40"
                  disabled={sendingCode}
                  data-testid="send-reset-code-button"
                >
                  {sendingCode ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sending code...</>
                  ) : (
                    <>Send Reset Code<ArrowRight className="w-5 h-5 ml-2" /></>
                  )}
                </Button>

                <p className="text-center text-slate-500 text-sm">
                  Remember your password?{" "}
                  <Link to="/login" className="text-violet-600 hover:text-amber-700 font-semibold transition-colors">
                    Sign in
                  </Link>
                </p>
              </div>
            </>
          )}

          {/* Step 2: Enter Code */}
          {step === 2 && (
            <>
              <div className="mb-8 text-center">
                <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-violet-600" />
                </div>
                <h1 className="text-3xl font-bold text-slate-900 font-['Manrope'] mb-2">Check your email</h1>
                <p className="text-slate-500">
                  We sent a 6-digit code to<br />
                  <span className="font-semibold text-slate-700">{email}</span>
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex justify-center gap-2">
                  {verificationCode.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(el) => (codeInputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(index, e)}
                      onPaste={handleCodePaste}
                      className="w-12 h-14 text-center text-2xl font-bold bg-slate-50 border-slate-200 focus:bg-white focus:border-violet-500 focus:ring-violet-500 rounded-xl transition-colors"
                      data-testid={`reset-code-input-${index}`}
                      disabled={verifying}
                    />
                  ))}
                </div>

                {verifying && (
                  <div className="flex items-center justify-center gap-2 text-violet-600">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Verifying...</span>
                  </div>
                )}

                <div className="text-center">
                  <p className="text-slate-500 text-sm mb-2">Did not receive the code?</p>
                  <Button
                    variant="ghost"
                    onClick={handleResendCode}
                    disabled={resendCooldown > 0 || sendingCode}
                    className="text-violet-600 hover:text-amber-700"
                    data-testid="resend-reset-code-button"
                  >
                    {sendingCode ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                    ) : resendCooldown > 0 ? (
                      <>Resend in {resendCooldown}s</>
                    ) : (
                      <><RefreshCw className="w-4 h-4 mr-2" />Resend code</>
                    )}
                  </Button>
                </div>

                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="w-full h-12 rounded-xl"
                  data-testid="back-to-email-button"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Change email
                </Button>
              </div>
            </>
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <>
              <div className="mb-8 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-emerald-600" />
                </div>
                <h1 className="text-3xl font-bold text-slate-900 font-['Manrope'] mb-2">Set New Password</h1>
                <p className="text-slate-500">Create a strong password for your account</p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-slate-700 font-medium">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a strong password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                      className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-emerald-500 focus:ring-emerald-500 pr-12 rounded-xl transition-colors"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400">Must be at least 6 characters</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-700 font-medium">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="h-12 bg-slate-50 border-slate-200 focus:bg-white focus:border-emerald-500 focus:ring-emerald-500 rounded-xl transition-colors"
                    data-testid="confirm-password-input"
                  />
                </div>
                
                <Button
                  type="submit"
                  className="w-full h-14 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40"
                  disabled={loading}
                  data-testid="reset-password-button"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Resetting...</>
                  ) : (
                    <>Reset Password<ArrowRight className="w-5 h-5 ml-2" /></>
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
